/**
 * useAgentSSE — SSE connection hook for agent chat streaming
 */
import { useState, useCallback, useRef } from 'react'
import { agentAPI, type SessionContext, type InteractionMode } from '@/lib/api/agent'

export interface SSEEvent {
  type: string
  data: any
  sequence?: number
}

interface UseAgentSSEReturn {
  isStreaming: boolean
  lastSequence: number
  sendMessage: (message: string, context: SessionContext, mode?: InteractionMode) => Promise<void>
  cancel: () => void
  onEvent: (handler: (event: SSEEvent) => void) => void
}

export function useAgentSSE(): UseAgentSSEReturn {
  const [isStreaming, setIsStreaming] = useState(false)
  const [lastSequence, setLastSequence] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const handlerRef = useRef<(event: SSEEvent) => void>(() => {})

  const cancel = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setIsStreaming(false)
  }, [])

  const sendMessage = useCallback(async (message: string, context: SessionContext, mode: InteractionMode = 'text') => {
    cancel()
    setIsStreaming(true)

    try {
      const response = await agentAPI.sendMessage(message, context, mode)

      // Handle non-SSE error responses (e.g. JSON error from CloudFront/API Gateway)
      const contentType = response.headers.get('content-type') || ''
      if (!contentType.includes('text/event-stream')) {
        try {
          const errorBody = await response.json() as any
          const errorMsg = errorBody?.error?.message || errorBody?.message || 'Failed to connect to AI assistant'
          handlerRef.current({ type: 'error', data: { message: errorMsg, code: errorBody?.error?.code || 'AGENT_SSE_ERROR' } })
        } catch {
          handlerRef.current({ type: 'error', data: { message: 'Failed to connect to AI assistant', code: 'AGENT_SSE_ERROR' } })
        }
        setIsStreaming(false)
        return
      }

      if (!response.body) { setIsStreaming(false); return }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        let eventType = ''
        let eventData = ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6).trim()
          } else if (line.startsWith('id: ')) {
            setLastSequence(parseInt(line.slice(4).trim(), 10))
          } else if (line === '' && eventType && eventData) {
            try {
              const parsed = JSON.parse(eventData)
              handlerRef.current({ type: eventType, data: parsed })
            } catch { /* skip malformed */ }
            eventType = ''
            eventData = ''
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        handlerRef.current({ type: 'error', data: { message: err.message, code: 'AGENT_SSE_ERROR' } })
      }
    } finally {
      setIsStreaming(false)
    }
  }, [cancel])

  const onEvent = useCallback((handler: (event: SSEEvent) => void) => {
    handlerRef.current = handler
  }, [])

  return { isStreaming, lastSequence, sendMessage, cancel, onEvent }
}
